import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsPencil, BsTrash, BsCheckLg, BsX, BsTrash2 } from 'react-icons/bs';
import { FiMoreVertical, FiTrash2 } from 'react-icons/fi';
import { AiOutlineLoading3Quarters, AiOutlineClear } from 'react-icons/ai';
import { Scrollbars } from 'react-custom-scrollbars-2';
import ReactDOM from 'react-dom';

// We can keep CustomScrollbar.css for other components if needed
import './CustomScrollbar.css';

const ConversationList = ({
    conversations,
    conversationId,
    loadConversation,
    editingConversationId,
    setEditingConversationId,
    editedConversationName,
    setEditedConversationName,
    handleSaveConversationName,
    setDeleteModalOpen,
    setConversationToDelete,
    // Props for infinite scrolling
    loadingMoreConversations,
    loadMoreConversations,
    hasMoreConversations,
    // New prop for handling bulk deletion
    onDeleteAllConversations,
    // Optional prop for loading indicator
    LoadingIndicator = () => <AiOutlineLoading3Quarters className="animate-spin h-4 w-4 text-gray-500" />,
    // New prop for starting a new conversation
    onStartNewConversation,
    // New prop for total conversations count
    totalConversations = 0
}) => {
    const [openMenuId, setOpenMenuId] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuButtonRefs = useRef({});
    const scrollbarRef = useRef(null);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollingTimeoutRef = useRef(null);

    // New state to track scrollbar size updates
    const [scrollbarHeight, setScrollbarHeight] = useState(0);
    const containerRef = useRef(null);

    // Initialize and update scrollbar height based on container
    useEffect(() => {
        const updateScrollbarHeight = () => {
            if (containerRef.current) {
                const containerHeight = containerRef.current.clientHeight;
                // Subtract height of debug info (25px)
                setScrollbarHeight(containerHeight - 25);
            }
        };

        // Initial update
        updateScrollbarHeight();

        // Add resize observer to handle container size changes
        const resizeObserver = new ResizeObserver(() => {
            updateScrollbarHeight();
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        // Also update when conversation list changes
        if (conversations && Array.isArray(conversations)) {
            updateScrollbarHeight();
        }

        // Cleanup
        return () => {
            if (containerRef.current) {
                resizeObserver.disconnect();
            }
        };
    }, [conversations]);


    // Handle scroll events from the custom scrollbar
    const handleScrollFrame = ({ scrollTop, scrollHeight, clientHeight }) => {
        // Show scrollbar when scrolling
        setIsScrolling(true);

        // Clear previous timeout
        if (scrollingTimeoutRef.current) {
            clearTimeout(scrollingTimeoutRef.current);
        }

        // Hide scrollbar after 1.5 seconds of inactivity
        scrollingTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 1500);

        // Load more when scrolled to bottom (with 50px threshold for earlier loading)
        if (!loadingMoreConversations && hasMoreConversations && scrollHeight - scrollTop - clientHeight < 50) {
            console.log('Scroll detected near bottom, loading more conversations');
            loadMoreConversations();
        }
    };

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (scrollingTimeoutRef.current) {
                clearTimeout(scrollingTimeoutRef.current);
            }
        };
    }, []);

    // Show scrollbar on mouse enter/leave
    const handleMouseEnter = () => setIsScrolling(true);
    const handleMouseLeave = () => {
        if (scrollingTimeoutRef.current) {
            clearTimeout(scrollingTimeoutRef.current);
        }
        scrollingTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 1000);
    };

    // Custom scrollbar thumb and track styling
    const renderThumb = ({ style, ...props }) => {
        const thumbStyle = {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
            cursor: 'grab',
            width: isScrolling ? '10px' : '6px', // Wider when scrolling/hovering
            transition: 'width 0.2s ease, background-color 0.2s ease',
        };

        return <div {...props} style={{ ...style, ...thumbStyle }} className="thumb-vertical" />;
    };

    const renderTrack = ({ style, ...props }) => {
        const trackStyle = {
            right: 0,
            bottom: 2,
            top: 2,
            borderRadius: '8px',
            width: '10px',  // Fixed width for track
            backgroundColor: 'transparent'
        };
        return <div {...props} style={{ ...style, ...trackStyle }} className="track-vertical" />;
    };

    // Berechne die Position des Menüs basierend auf dem Button
    const calculateMenuPosition = (buttonId) => {
        const buttonEl = menuButtonRefs.current[buttonId];
        if (!buttonEl) return;

        const rect = buttonEl.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + 5,
            left: rect.right - 172, // Menübreite ist 172px
        });
    };

    // Öffne Dropdown und berechne Position
    const toggleMenu = (id) => {
        const newId = openMenuId === id ? null : id;
        setOpenMenuId(newId);

        if (newId) {
            calculateMenuPosition(newId);
        }
    };

    // Schließe das Menü, wenn außerhalb geklickt wird
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId &&
                !event.target.closest('.dropdown-menu') &&
                !menuButtonRefs.current[openMenuId]?.contains(event.target)) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuId]);

    // Das Portal für das Dropdown-Menü
    const DropdownPortal = ({ children }) => {
        return openMenuId ? ReactDOM.createPortal(
            <div className="dropdown-menu" style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
            }}>
                {children}
            </div>,
            document.body
        ) : null;
    };

    const handleEditConversationName = (conv) => {
        setEditingConversationId(conv.conversation_id);
        setEditedConversationName(conv.name || '');
        setOpenMenuId(null);
    };

    const handleDeleteClick = (conv) => {
        setConversationToDelete(conv);
        setDeleteModalOpen(true);
        setOpenMenuId(null);
    };

    return (
        <div
            className="flex-1 w-full h-full flex flex-col"
            ref={containerRef}
        >
            {/* Debug Info with clean all button */}
            <div className="px-6 py-2 pt-2 text-xs text-gray-500 flex justify-between items-center border-b border-gray-100 bg-gray-50 mb-4">
                <span>
                {/* ? `${conversations.length}/${totalConversations} Gesprächen geladen` */}
                    {Array.isArray(conversations)
                        ? `Gesamt: ${totalConversations}`
                        : 'Keine Gespräche verfügbar'}
                </span>
                {Array.isArray(conversations) && conversations.length > 0 && (
                    <button
                        onClick={onDeleteAllConversations}
                        className="text-xs text-gray-500 hover:text-red-500 flex items-center"
                        title="Alle Gespräche löschen"
                    >
                        <span>Alle löschen</span>
                    </button>
                )}
            </div>

            {/* Re-implemented Scrollbars component with proper configuration */}
            <Scrollbars
                ref={scrollbarRef}
                autoHide
                autoHideTimeout={1000}
                autoHideDuration={200}
                renderThumbVertical={renderThumb}
                renderTrackVertical={renderTrack}
                onScrollFrame={handleScrollFrame}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                universal={true}
                style={{
                    width: '100%',
                    height: scrollbarHeight > 0 ? scrollbarHeight : 'calc(100% - 25px)',
                    minHeight: '200px',
                    flex: 1
                }}
            >

                <div className="pb-2 px-4">
                    {/* Neuer Button zum Starten einer Konversation */}
                    <motion.div
                        className="relative w-full rounded-lg"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <button
                            onClick={() => {
                                // Hier den startNewConversation aufrufen, den wir als prop übergeben müssen
                                if (typeof onStartNewConversation === 'function') {
                                    onStartNewConversation();
                                }
                            }}
                            className="flex items-center w-full p-3 rounded-lg transition-colors hover:bg-gray-100 text-gray-700"
                        >
                            <div className="flex items-center justify-center w-6 h-6 bg-blue-800 rounded-full mr-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <span className="text-blue-800 font-bold text-sm">Gespräch starten</span>
                        </button>
                    </motion.div>

                    {Array.isArray(conversations) && conversations.length > 0 ? (
                        conversations.map(conv => (
                            <motion.div
                                key={conv.conversation_id}
                                className="relative w-full rounded-lg group"
                                whileHover={{ scale: 1.0 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                {editingConversationId === conv.conversation_id ? (
                                    // Edit mode UI
                                    <div className="flex items-center bg-white shadow-sm rounded-lg p-1 border border-gray-100">
                                        <input
                                            type="text"
                                            value={editedConversationName}
                                            onChange={(e) => setEditedConversationName(e.target.value)}
                                            className="flex-1 p-2 text-left text-black rounded-md border-gray-200 focus:ring-blue-500 focus:border-blue-500"
                                            style={{ maxWidth: '155px' }}
                                            autoFocus
                                        />
                                        <div className="flex items-center">
                                            <button
                                                onClick={() => handleSaveConversationName(conv.conversation_id)}
                                                className="p-1 ml-1 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                                title="Speichern"
                                            >
                                                <BsCheckLg className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => setEditingConversationId(null)}
                                                className="p-1 ml-1 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                title="Abbrechen"
                                            >
                                                <BsX className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // Display mode UI
                                    <div className={`flex items-center justify-between rounded-lg transition-colors ${
                                        conv.conversation_id === conversationId
                                        ? 'bg-blue-50 text-blue-700 font-medium'
                                        : 'hover:bg-gray-100 text-gray-700'
                                    }`}>
                                        <button
                                            className="flex-1 text-left p-3 truncate"
                                            onClick={() => loadConversation(conv.conversation_id)}
                                        >
                                            {conv.name || "Unbenannte Konversation"}
                                        </button>

                                        <button
                                            ref={el => menuButtonRefs.current[conv.conversation_id] = el}
                                            onClick={() => toggleMenu(conv.conversation_id)}
                                            className="p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-700 rounded-full transition-colors opacity-100 md:opacity-0 group-hover:opacity-100"
                                        >
                                            <FiMoreVertical className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        ))
                    ) : (
                        <p className="text-gray-500 text-center py-4">Keine Konversationen gefunden</p>
                    )}

                    {/* "Load More" button only shows if there are more conversations to load */}
                    {hasMoreConversations && conversations.length > 0 && (
                        <motion.div
                            className="relative w-full rounded-lg my-2"
                            whileHover={{ scale: 1.0 }}
                            whileTap={{ scale: 0.99 }}
                        >
                            <button
                                onClick={loadMoreConversations}
                                disabled={loadingMoreConversations}
                                className="flex justify-center items-center w-full rounded-lg hover:bg-gray-100 text-blue-800 transition-colors px-3 py-2 text-sm font-bold"
                            >
                                {loadingMoreConversations ? (
                                    <div className="flex items-center">
                                        <LoadingIndicator />
                                        <span className="ml-2">Wird geladen...</span>
                                    </div>
                                ) : (
                                    "Mehr laden"
                                )}
                            </button>
                        </motion.div>
                    )}

                    {/* Loading indicator only when loading more conversations */}
                    {loadingMoreConversations && (
                        <div className="flex justify-center py-2">
                            <div className="flex flex-col items-center">
                                <span className="text-xs text-gray-500">Laden...</span>
                            </div>
                        </div>
                    )}

                    {/* Add space at the bottom for better scrolling */}
                    <div className="h-8"></div>
                </div>
            </Scrollbars>

            {/* Dropdown portal - fixed structure */}
            <DropdownPortal>
                <AnimatePresence>
                    {openMenuId && (
                        <motion.div
                            className="w-48 bg-white rounded-md shadow-lg py-1 border border-gray-100"
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.1, ease: "easeOut" }}
                        >
                            <button
                                onClick={() => {
                                    const conv = conversations.find(c => c.conversation_id === openMenuId);
                                    if (conv) handleEditConversationName(conv);
                                }}
                                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <BsPencil className="mr-3 w-4 h-4" />
                                Name bearbeiten
                            </button>
                            <button
                                onClick={() => {
                                    const conv = conversations.find(c => c.conversation_id === openMenuId);
                                    if (conv) handleDeleteClick(conv);
                                }}
                                className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                            >
                                <BsTrash className="mr-3 w-4 h-4" />
                                Löschen
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </DropdownPortal>
        </div>
    );
};

export default ConversationList;
